// incomingCall.js — THE PERSON CALLED RINGS (Nika 2026-10-23: "add calling
// mode into fly so you can call to anyone in workspace"). functions/
// callPerson.js makes the person called a MEMBER of a private room before
// anyone joins; this is their side: a banner (components/IncomingCallBanner.js,
// the meeting invite's own card) with the caller's name, Join call and Decline.
//
// A room rings here when someone else made it in the last two minutes, it has
// not ended, it is not the call this person is already in, they have not put
// it away — and they are one of its members. Seeing the room is NOT being
// called: every member of a workspace can see its private rooms
// (MeetService.assertCanAccessRoom on the server), so membership is asked
// once per room (meet's members list), and the answer remembered on the root
// under a double-underscore key (never state). The ring itself is ONE root key,
// `_callRing` ({ roomId, callerId, at }) or null: the banner reads it.
//
// The scan runs on the banner's own updates — the rooms changing (a room lands
// through meet's realtime the moment it is made) and the invite banner's 30 s
// tick (`_meetingInviteTick`), which lets a ring expire on an idle tab.
//
// FRANK: every export is self-contained (FA204); siblings through `this.call`.

export function incomingCallScan(el, s) {
  const host = el || this
  const root = (s && s.root) || (host.state && host.state.root)
  if (!root || typeof root.update !== 'function') return false
  const RING_MS = 2 * 60 * 1000
  const now = Date.now()
  const mine = [root.currentUserSupabaseId, root.currentUserId].filter(Boolean).map(String)
  const memo = (root.__callRing = root.__callRing || { member: {}, away: {} })
  const ringing = (room) =>
    !!room &&
    room.visibility === 'private' &&
    !room.endedAt &&
    !!room.createdBy &&
    !mine.includes(String(room.createdBy)) &&
    room.id !== root.activeRoomId &&
    !memo.away[room.id] &&
    now - (Date.parse(room.createdAt || '') || 0) < RING_MS
  const rooms = (Array.isArray(root.rooms) ? root.rooms : []).filter(ringing)
  // a ring that no longer qualifies (ended, joined, expired, put away) goes
  const ring = root._callRing
  if (ring && !rooms.some((r) => r.id === ring.roomId)) {
    root.update({ _callRing: null }, { preventFetch: true })
  }
  if (!mine.length) return false
  const fns = (host.context && host.context.functions) || {}
  if (typeof fns.listMeetMembers !== 'function') return false
  for (const room of rooms) {
    if (memo.member[room.id] !== undefined) {
      if (memo.member[room.id] === true && !root._callRing) {
        root.update(
          {
            _callRing: {
              roomId: room.id,
              callerId: String(room.createdBy),
              at: Date.parse(room.createdAt || '') || now
            }
          },
          { preventFetch: true }
        )
      }
      continue
    }
    memo.member[room.id] = 'asking'
    Promise.resolve(host.call('listMeetMembers', room.id)).then(
      (rows) => {
        const member = (Array.isArray(rows) ? rows : []).some((m) =>
          mine.includes(String((m && m.userId) || ''))
        )
        memo.member[room.id] = member
        if (member && ringing(room) && !root._callRing) {
          root.update(
            {
              _callRing: {
                roomId: room.id,
                callerId: String(room.createdBy),
                at: Date.parse(room.createdAt || '') || Date.now()
              }
            },
            { preventFetch: true }
          )
        }
      },
      () => {
        memo.member[room.id] = false
      }
    )
  }
  return true
}

// The ring on screen, with the caller's name (the roster by user id; else the
// room's own name), or null.
export function incomingCallOf(root) {
  const r = root && typeof root === 'object' ? root : {}
  const ring = r._callRing
  if (!ring || !ring.roomId) return null
  const people = Array.isArray(r.people) ? r.people : []
  const caller = people.find((p) => p && String(p.userId || '') === String(ring.callerId))
  const room = (Array.isArray(r.rooms) ? r.rooms : []).find((x) => x && x.id === ring.roomId)
  return {
    roomId: ring.roomId,
    callerName: (caller && (caller.name || caller.email)) || (room && room.name) || '',
    at: ring.at || 0
  }
}

// Join call: into the room, on /meet — the same way in as the caller's.
export function incomingCallJoin() {
  const root = this.state && this.state.root
  const ring = root && root._callRing
  if (!ring || !ring.roomId) return false
  const memo = (root.__callRing = root.__callRing || { member: {}, away: {} })
  memo.away[ring.roomId] = true
  root.update({ _callRing: null, activeRoomId: ring.roomId, meetView: 'call' })
  this.router('/meet', this.getRoot())
  if (typeof window !== 'undefined' && window.history) {
    // Hash-only, on the address the route above just wrote, so the call keeps
    // the tab's `/w/<org>/<workspace>` (F2) — the stamp callPerson makes, and
    // why it stays a raw write (functions/callPerson.js).
    // frank-allow FA410
    window.history.replaceState(null, '', '#room=' + ring.roomId + '&view=call')
  }
  this.call('connectCall', ring.roomId)
  return true
}

// Decline: the banner goes, and this room does not ring again here.
export function incomingCallDecline() {
  const root = this.state && this.state.root
  const ring = root && root._callRing
  if (!ring || !ring.roomId) return false
  const memo = (root.__callRing = root.__callRing || { member: {}, away: {} })
  memo.away[ring.roomId] = true
  root.update({ _callRing: null }, { preventFetch: true })
  return true
}
